import type { Combatant } from "./combatant";
import type { Side } from "./side";
import type { EncounterState } from "./state";
import { activeActor } from "./turn";

/**
 * The canonical **capability predicates** — the only sanctioned way rules code asks what
 * a combatant can do.
 *
 * Dispatch on capability presence through these, **never by enumerating a combatant's
 * `kind` discriminator**. A new entity type should cost a spawn adapter, not a predicate
 * sweep across the rules layer. `isCharacter`/`isActor` are the two sanctioned kind reads
 * in this package (their own definitions); everything else here reads a frozen capability
 * off `base`.
 */

/**
 * The {@link Side} a combatant fights on — reads the frozen
 * {@link CombatantBase.allegiance} component, no `kind` read at all. Every spawn adapter
 * stamps it: `mech`/`pilot` → `"pc"`; `npc` → `"npc"`.
 *
 * **`object`/`deployable` are NOT a flat `"npc"`.** A *deployed* one — Jericho cover, a
 * turret drone, a mine — takes the **deployer's** side, passed in at spawn
 * (`SpawnObjectArgs.allegiance`); only authored scenery with no deployer (a wall, a
 * boulder, a wreck) keeps the `"npc"` default. Without that, a player's own cover reads
 * hostile to them and the built-in Overwatch compiler offers it the shot against their own
 * units. The stamp is still **frozen at spawn**: it is the deployer's side *at deploy
 * time*, not a live re-read.
 *
 * `drone` is likewise not a flat per-kind constant — it is computed at spawn from the
 * deploying player's {@link Combatant.controllerSlotId}: a set slot (player-owned) →
 * `"pc"`; `null`/`undefined` (GM/NPC-owned) → `"npc"`.
 *
 * **The stamp is a spawn snapshot while `controllerSlotId` is reassignable**, via the
 * GM-only `set_controller` command. Reassigning a drone's controller does **NOT**
 * auto-update its stamped `allegiance` — the reducer never re-derives this field, by
 * design, because `set_controller`'s lossless inverse depends on "an omitted `allegiance`
 * means leave the current side alone". Concretely: handing a drone to a new controller
 * without also passing `allegiance` leaves the drone reading its **spawn-time** side,
 * which can then be stale relative to who controls it. **The fix is explicit, not
 * automatic**: pass `allegiance` in the SAME `set_controller` command that reassigns
 * `controllerSlotId`. Pinned by `setController.test.ts`'s "drone allegiance is a spawn
 * snapshot" block. **Accepted edge:** a GM-controlled *ally* drone stamps `"npc"` at
 * spawn — the same standing GM-ally limitation pilots already accept, with
 * `set_controller`'s `allegiance` field as the escape hatch.
 */
export function sideOf(c: Combatant): Side {
  return c.base.allegiance;
}

/**
 * **The one control predicate** — does the slot `slot` control `combatant`? This is the
 * single derivation behind every "does slot S control C?" question in the codebase, so
 * the server's wall and the client's affordance cannot drift apart. Widening control to
 * more than one slot is a change in this function rather than a sweep of hand-written
 * `=== actorId` comparisons.
 *
 * **Combatant-level, not state-level, deliberately.** Call sites that hold a `Combatant`
 * but no `EncounterState` would not be reachable by a `(state, combatantId, slot)`
 * signature. Callers that hold only an id do the lookup themselves and pass `undefined`
 * through — an id naming no combatant is controlled by nobody.
 *
 * **A `null`/`undefined` slot controls nothing.** `controllerSlotId: null` means
 * *GM-owned* — the absence of a controlling slot, not a slot named "null" — so a viewer
 * with no slot identity must not match a GM-owned combatant here. Sites that want "the GM
 * is the audience for an unowned unit" state that separately and explicitly; this
 * predicate answers only the slot-membership half.
 *
 * **Control is the owner of record OR any co-driver**: a slot controls the combatant if
 * it is {@link Combatant.controllerSlotId} or appears in
 * {@link Combatant.coControllerSlotIds}. The asymmetry is deliberate and lives one level
 * up: this predicate treats the two fields identically (control is control), while every
 * *"which slot **owns** it"* site keeps reading the scalar alone.
 */
export function controlsCombatant(
  combatant: Pick<Combatant, "controllerSlotId" | "coControllerSlotIds"> | undefined,
  slot: string | null | undefined,
): boolean {
  if (combatant === undefined || slot === undefined || slot === null) return false;
  if (combatant.controllerSlotId === slot) return true;
  return combatant.coControllerSlotIds?.includes(slot) ?? false;
}

/**
 * **Is `slot` the pre-activation controller of `combatant`?** — the one test for "would a
 * first action by this caller on this `ready` unit start its turn". It is
 * {@link controlsCombatant}, plus the GM for **every** unit. `token-command-menu` 2c (developer,
 * 2026-09-24): taking any action on a ready unit starts its turn, with no separate "activate"
 * step. The GM's reach widened from GM-owned units to all of them on 2026-09-28 (developer: "let
 * anyone with control of a unit start that unit's turn"), drags included: "Accidental turn start
 * can be fixed with the undo button, so would rather the happy path be smoother."
 *
 * `isGm` means "the GM, asking to start the turn": the room passes it only for an intent carrying
 * `startTurn`, so the CLI and GM adjudication keep the wave-through
 * (`backend/src/encounterActivationFold.ts`). Both the room's fold and the browser's preview
 * (`frontend/src/lib/encounter/activationPreview.ts`) read this function, so the two cannot drift.
 */
export function controlsActivation(
  combatant: Pick<Combatant, "controllerSlotId" | "coControllerSlotIds"> | undefined,
  slot: string | null | undefined,
  isGm: boolean,
): boolean {
  if (combatant === undefined || slot === undefined || slot === null) return false;
  return isGm || controlsCombatant(combatant, slot);
}

/**
 * **Does slot `slot` see `combatant` in full?** — the one derivation for full-sheet
 * visibility, the sibling of {@link controlsCombatant} and deliberately *not* the same
 * predicate.
 *
 * **Members: the owner of record OR a visibility grantee** —
 * {@link Combatant.controllerSlotId}, or an entry in
 * {@link Combatant.visibilityGrantSlotIds}. **{@link Combatant.coControllerSlotIds} is NOT
 * a member, on purpose.** `decisions/0179` (developer ruling point 6, 2026-09-19):
 * visibility is not derived from control. A control grant lets a slot drive a unit and
 * changes nothing else; seeing its sheet is its own, independently revocable grant. The
 * owner of record still implies visibility — point 6 is about *grants*, and a unit on your
 * own roster you cannot see is not a state the table wants (`unit-control-assignment` 1.4
 * invariant #4 pinned that branch, and that pin stands).
 *
 * **A `null`/`undefined` slot sees nothing**, exactly as in {@link controlsCombatant}:
 * `controllerSlotId: null` is the *absence* of a controlling slot, not a slot named "null,"
 * so a viewer with no slot identity must never match a GM-owned combatant here. The GM's
 * own see-everything rule is stated separately at each call site (`role === "gm"`), above
 * this predicate; this one answers only the slot-membership half.
 *
 * **Combatant-level, not state-level**, for the same reason as {@link controlsCombatant}:
 * callers holding a `Combatant` and no `EncounterState` must be able to reach it.
 *
 * **Exactly two call sites**, both in `../projection` — `visibleTo` (unit-level omission,
 * on the wire) and `projectCombatantFor` (field-level disclosure masking, display-only per
 * `decisions/0136`). If you are about to add a third that means "may this slot act," you
 * want {@link controlsCombatant} instead.
 */
export function hasFullView(
  combatant: Pick<Combatant, "controllerSlotId" | "visibilityGrantSlotIds"> | undefined,
  slot: string | null | undefined,
): boolean {
  if (combatant === undefined || slot === undefined || slot === null) return false;
  if (combatant.controllerSlotId === slot) return true;
  return combatant.visibilityGrantSlotIds?.includes(slot) ?? false;
}

/**
 * **Every slot that controls `combatant`** — the owner of record first (when there is
 * one), then the co-drivers. The enumerating sibling of {@link controlsCombatant}: that
 * one answers "does S control C?", this one answers "who do I have to tell?", which is
 * what routing needs.
 *
 * Empty for a GM-owned unit with no co-drivers — callers that want the GM as a fallback
 * audience add that themselves. Never contains duplicates: `set_controller`'s reducer
 * normalizes the co-list against the owner on every write, so the two fields can never
 * name the same slot.
 */
export function controllersOf(
  combatant: Pick<Combatant, "controllerSlotId" | "coControllerSlotIds"> | undefined,
): string[] {
  if (combatant === undefined) return [];
  const owner = combatant.controllerSlotId;
  return [...(owner != null ? [owner] : []), ...(combatant.coControllerSlotIds ?? [])];
}

/**
 * Whether a combatant is a **character** — tech-targetable, makes saves, can be hit by an
 * AoE/blast — per RAW's "objects aren't characters: no turns, no E-Defense, no saves":
 * `kind` ∈ `mech`/`npc`/`pilot`/`drone`, as opposed to `object`/`deployable` (terrain-like
 * spatial props, never characters). **This is the canonical character-lane predicate** —
 * the tech-target reject, the forced-save reject, and the AoE enumeration all gate on it.
 *
 * **`drone` is the kind where character ≠ actor**: a drone IS a character (Lock On / Scan
 * it, rolls +0 saves) but is NOT an {@link isActor actor} (no turn). Do not conflate the
 * two — a site deciding "is this thing targetable/save-able" wants `isCharacter`, a site
 * deciding "does this thing take a turn" wants `isActor`.
 */
export function isCharacter(c: Combatant): boolean {
  return c.kind === "mech" || c.kind === "npc" || c.kind === "pilot" || c.kind === "drone";
}

/**
 * Whether a combatant is an **object** — RAW's "objects aren't characters" read literally,
 * the exact complement of {@link isCharacter} over `CombatantKind`'s six values. The
 * shared "is this an object, for a burst/blast that auto-hits objects separately from
 * characters" predicate. `movement.ts` keeps its own local `isObjectKind` on purpose — the
 * movement lane is deliberately decoupled from this one.
 */
export function isObject(c: Combatant): boolean {
  return !isCharacter(c);
}

/**
 * Whether a combatant is an **actor** — a character that takes turns: `kind` ∈
 * `mech`/`npc`/`pilot`, as opposed to `object`/`deployable` (terrain-like spatial props)
 * **and** `drone` (a character that never gets a turn — RAW "acts on your turn"). This is
 * the single predicate the **activation lane** filters on, so a placed object or a drone
 * never enters the turn tracker, never shows a `perUnitState` row, and never blocks
 * `roundComplete` — a selector filter, NOT an `order` carve-out (both stay in
 * `state.order`, which doubles as display order and the remove-inverse index).
 *
 * **`!isActor` does not mean "non-character."** `drone` is `!isActor` but IS a character,
 * so every consumer that means "is this a character" (targetable by tech, makes saves, hit
 * by a blast) must use {@link isCharacter}, never `!isActor`. `isActor` is also reused,
 * deliberately, by the rules-side spatial and hide gates that must exclude non-actors —
 * so "purely activation-lane" is the wrong test for whether a new use is legitimate; the
 * test is whether the site means "does this thing take a turn."
 *
 * **Deliberately NOT reused by the movement-blocking seam**: the character/Size-gated
 * pass-through classification keeps its own local kind predicate (`movement.ts`'s
 * `isCharacterKind`) — two independently-evolving lanes, kept decoupled on purpose, and
 * `drone`'s occupancy membership there is its own explicit call, not a silent ride on this
 * predicate.
 */
export function isActor(c: Combatant): boolean {
  return c.kind === "mech" || c.kind === "npc" || c.kind === "pilot";
}

/**
 * Whether a combatant carries a **heat track** — `base.maxHeat !== undefined`. **This
 * predicate is the ONE place that presence should ever be read.** Mechs and heat-bearing
 * NPCs (frame/class `heatcap`) carry one; a Biological NPC (Core p.282's tech-immunity
 * signal), a pilot on foot, and every object/deployable/drone/mine omit `base.maxHeat`
 * (and `current.heat`) entirely and have none. The heat cascade and the heat-routing
 * resolver dispatch on this rather than enumerating kinds.
 */
export function hasHeatTrack(c: Combatant): boolean {
  return c.base.maxHeat !== undefined;
}

/**
 * Whether a combatant carries a **structure track** — `base.maxStructure !== undefined`.
 * **This predicate is the ONE place that presence should ever be read.** Mechs and NPC
 * mechs carry one (the HP consequence track); a pilot on foot and every
 * object/deployable/drone/mine omit `base.maxStructure` entirely and have none (straight
 * HP-to-zero destroy). The destruction cascade dispatches on this rather than a three-way
 * kind fork.
 */
export function hasStructureTrack(c: Combatant): boolean {
  return c.base.maxStructure !== undefined;
}

/**
 * Whether a combatant carries a **stress track** — `base.maxStress !== undefined`. The
 * heat-side twin of {@link hasStructureTrack}: mechs/NPC mechs carry one, everything
 * without a heat track has none.
 */
export function hasStressTrack(c: Combatant): boolean {
  return c.base.maxStress !== undefined;
}

/**
 * Whether a combatant goes **Down and Out** at 0 HP (`destructionOutcome: "downAndOut"`): a pilot
 * on foot. RAW's "an adjacent pilot" (Patch, Corrective) means this, not a mech that happens to
 * carry the pilot's gear.
 */
export function goesDownAndOut(c: Combatant): boolean {
  return c.base.destructionOutcome === "downAndOut";
}

/**
 * The **save roll bonus** a combatant adds to a `d20` when it makes a save on the named HASE
 * `axis` — `"hull" | "agility" | "systems" | "engineering"`, or any other label an effect
 * happens to carry. **This function is the ONE place a save bonus is resolved**; no resolver
 * may read `base.saveBonus` or `base[axis]` directly, or the axis silently stops mattering
 * (which is exactly how the pre-`decisions/0163` engine ended up rolling the save TARGET as
 * the bonus and auto-passing every save in the game).
 *
 * The order is `base.saveBonus ?? base[axis] ?? 0`, and each step has a RAW warrant
 * (citations are `reference/lancer-core-book.txt` line numbers, verified line-by-line):
 *
 * 1. **{@link CombatantBase.saveBonus} first** — the axis-less flat bonus RAW grants exactly
 *    two kinds: the on-foot pilot's GRIT (`:3970`) and the drone's `+0` (`:5777`). It is an
 *    override precisely because those two kinds do *not* roll their axes.
 * 2. **Then the named axis** — "when taking actions that rely on a mech, mech skills are
 *    used instead" (`:1093`), and Metahook's "may use your SYSTEMS to make skill checks and
 *    saves" (`:9777`) only parses if the mech skill *is* the bonus. NPC HASE are signed
 *    per-tier roll bonuses and go negative (Aegis T1 `Agility: -2`, `:14142-14143`), so this
 *    step must preserve sign rather than clamp.
 * 3. **Then `+0`** — "You can always roll 1d20 with no bonuses against a target number of
 *    10" (`:1090`). Absent axis means absent bonus, never a crash and never a guess.
 *
 * An unrecognized `axis` label (or one naming a non-numeric field) falls through to `0` by
 * the same rule; nothing here throws.
 */
export function saveBonusFor(c: Combatant, axis: string): number {
  if (c.base.saveBonus !== undefined) return c.base.saveBonus;
  const onAxis = (c.base as unknown as Record<string, unknown>)[axis];
  return typeof onAxis === "number" ? onAxis : 0;
}

/**
 * Whether a combatant **contributes to terrain** — carries an authored `objectProfile`: a
 * placed object/deployable with a spatial profile
 * (`blocksMovement`/`blocksSight`/`cover`/`moveCost`) that the sight/cover and
 * movement-cost closures fold in alongside `terrain.ts`'s map-authored flags.
 * `objectProfile` is exclusive to the `object`/`deployable` kinds — only
 * `spawnObject`/`spawnObjectGroup` ever set it — and optional even among them (a purely
 * cosmetic/inert prop omits it), so this is a presence check, not a kind read.
 */
export function contributesToTerrain(c: Combatant): boolean {
  return c.base.objectProfile !== undefined;
}

/**
 * Whether no rules-driven forced move may shift `c` (`objectProfile.rooted`, `decisions/0193`
 * point 5): a scale tree ignores a Knockback, a pull, and the rig's knockback and shunt. Each
 * forced-move producer checks this before it moves anything; a GM placement ignores it.
 */
export function isRooted(c: Combatant): boolean {
  return c.base.objectProfile?.rooted === true;
}

/**
 * Whether `c` is a **map asset**: an object spawned from the catalogue, carrying an `assetId`
 * (`decisions/0193` point 3; only `spawnAsset` sets it, so no kind read is needed). A Terraforming rig treats one as no obstacle and removes it when its
 * swept band touches it (`projects/terraformer-clears-assets.md`). An unknown id still counts: the
 * id marks catalogue provenance whether or not the art survives.
 */
export function isMapAsset(c: Combatant): boolean {
  return c.assetId !== undefined;
}

/** The warning a vetoed forced move on a rooted object records — one wording for every producer. */
export function rootedVetoWarning(c: Combatant, what: string): string {
  return `${c.name}: rooted — can't be ${what}; forced move not applied.`;
}

/**
 * **Object-group members** — every combatant carrying `objectGroupId === groupId`, sorted
 * by id for deterministic (replay-stable) iteration. This is the query the group-delete
 * command builder reads to remove an authored multi-section object as one thing; combat
 * destroys sections independently and never calls this. An unknown/empty `groupId` yields
 * `[]`, never a crash.
 */
export function objectGroupMembers(state: EncounterState, groupId: string): Combatant[] {
  return Object.values(state.combatants)
    .filter((c) => c.objectGroupId === groupId)
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * The **un-acted units on a given side**, in display order. Derived on demand: every
 * combatant in `order` whose {@link sideOf} matches `side` and that is neither in
 * `activation.activated` (already ended its turn) **nor** the one currently mid-turn.
 *
 * **Does not** filter for ability (alive/SHUT DOWN) or actorhood (object/deployable/drone) —
 * that is `ableUnactedOnSide` in `../activation`, which the round-end/alternation legality
 * *and* the CLI/rail displays all read. This function is the raw building block
 * `ableUnactedOnSide` filters; it has **no display consumer of its own**
 * (`projects/activation-count-readouts.md`) — a destroyed-but-un-acted unit and a placed
 * object/wreck both pass through it unfiltered, which is exactly the shape a display needs
 * to filter away, not render. A combatant absent from `order` (defensive) is skipped; a
 * destroyed/removed unit is naturally gone.
 */
export function unactedOnSide(state: EncounterState, side: Side): Combatant[] {
  const acted = new Set(state.activation.activated);
  const acting = activeActor(state.activation);
  const out: Combatant[] = [];
  for (const id of state.order) {
    if (acted.has(id) || id === acting) continue;
    const c = state.combatants[id];
    if (c && sideOf(c) === side) out.push(c);
  }
  return out;
}
